import { useEffect, useState, useCallback, useRef } from 'react';
import { useOkrSwitcher, OkrAccordionSkeleton } from '@workboard/ui-okr';
import { Typography, ThemeProvider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Chevron from '../../images/dropdown-18-px.svg';
import { Box, Flex } from '../ui';
import OkrService from '../../services/okrs';
import { LazyObjectiveCardList } from './LazyObjectiveCardList';
import { filterObjectivesByType } from '../../utils/transformMeetingObjectives';
import { ViewMoreButton } from './ViewMoreButton';
import { GlobalStyle, theme } from './../../../../components/theme/theme';
import { MeetingOkrsTooltip } from './MeetingOkrsTooltip';

export const MeetingObjectivesSection = ({
  isLoadingData,
  response,
  directReporteeId,
  label,
  type,
  currentUser = null,
  selectedUser = null,
  isOneOnOne = false,
  meeting = null,
  open = false,
  isPrint = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(open);
  const [isLoading, setIsLoading] = useState(true);
  const [rawObjectives, setRawObjectives] = useState([]);
  const [rawManagerObjectives, setRawManagerObjectives] = useState([]);
  const [rawReporteeObjectives, setRawReporteeObjectives] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [managerObjectives, setManagerObjectives] = useState([]);
  const [reporteeObjectives, setReporteeObjectives] = useState([]);
  const [initIndexEnd, setInitIndexEnd] = useState(0);
  const [initManagerIndexEnd, setManagerInitIndexEnd] = useState(0);
  const [initReporteeIndexEnd, setReporteeInitIndexEnd] = useState(0);
  const { isExpanded, handleChange } = useOkrSwitcher({
    defaultExpanded: isPrint,
    persist: true,
    persistedKey: 'secondaryPage.objectives',
  });
  const objectiveKrsRef = useRef(new Map());
  const oneOnOneStyles = isOneOnOne ? { padding: 0, marginTop: 0, marginBottom: '7px' } : {};

  const showMore = rawObjectives.length > initIndexEnd && !isPrint;
  const noData = !rawObjectives.length;
  const wrapperStyle = isPrint ? 'm-print-sections-wrapper' : 'm-details-extra-details';

  const showMoreManager = rawManagerObjectives.length > initManagerIndexEnd && !isPrint;
  const showMoreReportee = rawReporteeObjectives.length > initReporteeIndexEnd && !isPrint;
  const noDataManager = !rawManagerObjectives.length;
  const noDataReportee = !rawReporteeObjectives.length;

  const isOneOneOneData = isOneOnOne && meeting && currentUser;
  let managerName = '';
  let participantName = '';
  if (isOneOneOneData) {
    managerName = directReporteeId == currentUser?.id ? selectedUser?.firstname : currentUser?.firstname;
    participantName = directReporteeId == currentUser?.id ? currentUser?.firstname : selectedUser?.firstname;
  }

  const handleToggle = () => setOpen(!open);

  const handleData = async () => {
    if (isOneOnOne) {
      const { manager, directReportee } = response;
      if (!manager.err) {
        // In a 1-on-1, exclude the manager's personal (individual) objectives; only show their team objectives.
        const { sliceIndexEnd, filteredObjectives } = filterObjectivesByType(manager.data, 'team');
        const objectivesSlice = isPrint ? filteredObjectives : filteredObjectives.slice(0, sliceIndexEnd);
        setRawManagerObjectives(filteredObjectives);
        setManagerObjectives(objectivesSlice);
        setManagerInitIndexEnd(sliceIndexEnd);
      }
      if (!directReportee.err) {
        const { sliceIndexEnd, filteredObjectives } = filterObjectivesByType(directReportee.data);
        const objectivesSlice = isPrint ? filteredObjectives : filteredObjectives.slice(0, sliceIndexEnd);
        setRawReporteeObjectives(filteredObjectives);
        setReporteeObjectives(objectivesSlice);
        setReporteeInitIndexEnd(sliceIndexEnd);
      }
    } else {
      const [err, data] = response;
      if (!err) {
        const { sliceIndexEnd, filteredObjectives } = filterObjectivesByType(data, type);
        const objectivesSlice = isPrint ? filteredObjectives : filteredObjectives.slice(0, sliceIndexEnd);
        setRawObjectives(filteredObjectives);
        setObjectives(objectivesSlice);
        setInitIndexEnd(sliceIndexEnd);
      }
    }
    setIsLoading(false);
  };

  const getStoreData = useCallback(
    (okrId) => {
      return objectiveKrsRef.current.get(okrId) ?? [];
    },
    [objectiveKrsRef.current]
  );

  const loadKrsAndDispatch = useCallback(
    async (okr, setLoading) => {
      const okrId = okr.id;
      if (objectiveKrsRef.current.has(okrId)) {
        return [objectiveKrsRef.current.get(okrId), false];
      }

      if (setLoading) setLoading(true);
      let response;
      const krType = isOneOnOne ? '' : type;
      if (!isOneOnOne) {
        const excludePersonalKrs = krType === 'team' ? true : false;
        const params = {
          meetingId: meeting?.id,
          attendeeId: directReporteeId,
          goalId: okrId,
          goalTeam: okr.teamName,
          excludePersonalKrs,
        };
        response = await OkrService.getMeetingKeyResults(params);
      } else {
        response = await OkrService.getObjectivesKeyResults({ userId: directReporteeId, okr, krType });
      }
      return [response, true];
    },
    [objectiveKrsRef.current, directReporteeId]
  );

  useEffect(() => {
    if (!isLoadingData) handleData();
  }, [isLoadingData]);

  if (isPrint && !isLoading && !objectives.length) return null;

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Box className={wrapperStyle} style={{ marginTop: '7px', ...oneOnOneStyles }}>
        <Flex
          className="m-unexpanded-details"
          onClick={handleToggle}
          style={{ borderRadius: open ? '4px 4px 0 0' : '4px' }}
        >
          <Box>
            <span className="m-extra-details-text">{label}</span>
            {!isOneOnOne && !isPrint && (<MeetingOkrsTooltip/>)}
          </Box>
          {!isPrint && (
            <Box className="m-extra-details-chevron">
              <Chevron className="m-chevron" style={{ transform: open ? 'rotate(0deg)' : 'rotate(-180deg)' }} />
            </Box>
          )}
        </Flex>
        {open && isLoading && (
          <Flex className="m-expanded-details">
            <Box style={{ flexGrow: 4 }}>
              <OkrAccordionSkeleton />
              <OkrAccordionSkeleton />
            </Box>
          </Flex>
        )}
        {!isOneOnOne && open && !isLoading && noData && (
          <Flex className="m-expanded-details">
            <Box style={{ flexGrow: 4, paddingLef: 5, marginRight: -15, paddingBottom: 5 }}>
              <Typography variant="subtitle1">{t('No Open Objectives')}</Typography>
            </Box>
          </Flex>
        )}
        {open && !isLoading && (
          <Flex className="m-expanded-details">
            <Box style={{ flexGrow: 4, paddingLef: 5, marginRight: -15, color: '#333943' }}>
              {isOneOnOne && (
                <>
                  <>
                    <div className="meet-objective-ownername truncateText left">{t('{{name}}\'s OKRs', { name: managerName })}</div>
                    <>
                      {noDataManager && <Typography variant="subtitle1">{t('No Open Objectives')}</Typography>}
                      {!noDataManager && (
                        <>
                          <LazyObjectiveCardList
                            data={managerObjectives}
                            isLoading={isLoading}
                            isExpanded={isExpanded}
                            onExpandChange={handleChange}
                            krsLoader={loadKrsAndDispatch}
                            initialKrs={getStoreData}
                          />
                          {showMoreManager && (
                            <ViewMoreButton
                              label={t('Objectives')}
                              rawData={rawManagerObjectives}
                              pageData={managerObjectives}
                              indexEnd={initManagerIndexEnd}
                              updateViewData={setManagerObjectives}
                            />
                          )}
                        </>
                      )}
                    </>
                  </>
                  <>
                    <div className="meet-objective-ownername truncateText left">{t('{{name}}\'s OKRs', { name: participantName })}</div>
                    <>
                      {noDataReportee && <Typography variant="subtitle1">{t('No Open Objectives')}</Typography>}
                      {!noDataReportee && (
                        <>
                          <LazyObjectiveCardList
                            data={reporteeObjectives}
                            isLoading={isLoading}
                            isExpanded={isExpanded}
                            onExpandChange={handleChange}
                            krsLoader={loadKrsAndDispatch}
                            initialKrs={getStoreData}
                          />
                          {showMoreReportee && (
                            <ViewMoreButton
                              label={t('Objectives')}
                              rawData={rawReporteeObjectives}
                              pageData={reporteeObjectives}
                              indexEnd={initReporteeIndexEnd}
                              updateViewData={setReporteeObjectives}
                            />
                          )}
                        </>
                      )}
                    </>
                  </>
                </>
              )}
              {!isOneOnOne && (
                <>
                  <LazyObjectiveCardList
                    data={objectives}
                    isLoading={isLoading}
                    isExpanded={isExpanded}
                    onExpandChange={handleChange}
                    krsLoader={loadKrsAndDispatch}
                    initialKrs={getStoreData}
                  />
                  {showMore && (
                    <ViewMoreButton
                      label={t('Objectives')}
                      rawData={rawObjectives}
                      pageData={objectives}
                      indexEnd={initIndexEnd}
                      updateViewData={setObjectives}
                    />
                  )}
                </>
              )}
            </Box>
          </Flex>
        )}
      </Box>
    </ThemeProvider>
  );
};
